export interface StatusConfig {
  key: string;
  label: string;
  color: string;
  bg: string;
  border: string;
  badgeColor: string;
  hex: string;
}

const STATUS_MAP: Record<string, StatusConfig> = {
  scheduled: {
    key: 'scheduled',
    label: 'Planifié',
    color: 'text-blue-700',
    bg: 'bg-blue-100',
    border: 'border-blue-200',
    badgeColor: 'blue',
    hex: '#3B82F6',
  },
  confirmed: {
    key: 'confirmed',
    label: 'Confirmé',
    color: 'text-cyan-700',
    bg: 'bg-cyan-100',
    border: 'border-cyan-200',
    badgeColor: 'cyan',
    hex: '#06B6D4',
  },
  showed: {
    key: 'showed',
    label: 'Effectué',
    color: 'text-emerald-700',
    bg: 'bg-emerald-100',
    border: 'border-emerald-200',
    badgeColor: 'green',
    hex: '#10B981',
  },
  cancelled: {
    key: 'cancelled',
    label: 'Annulé',
    color: 'text-red-700',
    bg: 'bg-red-100',
    border: 'border-red-200',
    badgeColor: 'red',
    hex: '#EF4444',
  },
  rescheduled: {
    key: 'rescheduled',
    label: 'Reporté',
    color: 'text-amber-700',
    bg: 'bg-amber-100',
    border: 'border-amber-200',
    badgeColor: 'yellow',
    hex: '#F59E0B',
  },
  no_reply: {
    key: 'no_reply',
    label: 'Pas de réponse',
    color: 'text-gray-700',
    bg: 'bg-gray-100',
    border: 'border-gray-300',
    badgeColor: 'gray',
    hex: '#6B7280',
  },
  no_show: {
    key: 'no_show',
    label: 'No-show',
    color: 'text-rose-800',
    bg: 'bg-rose-100',
    border: 'border-rose-300',
    badgeColor: 'red',
    hex: '#9F1239',
  },
};

const FALLBACK: StatusConfig = {
  key: 'unknown',
  label: 'Inconnu',
  color: 'text-gray-600',
  bg: 'bg-gray-50',
  border: 'border-gray-200',
  badgeColor: 'gray',
  hex: '#9CA3AF',
};

function normalize(raw: string): string {
  const s = raw.toLowerCase().trim();

  if (s === 'scheduled' || s === 'planifié' || s === 'planifie') return 'scheduled';
  if (s === 'confirmed' || s === 'confirmé' || s === 'confirme') return 'confirmed';
  if (s === 'completed' || s === 'effectué' || s === 'effectue' || s === 'showed') return 'showed';
  if (s === 'cancelled' || s === 'annulé' || s === 'annule') return 'cancelled';
  if (s === 'postponed' || s === 'reporté' || s === 'reporte' || s === 'rescheduled') return 'rescheduled';
  if (s === 'no_show' || s === 'no-show' || s === 'noshow') return 'no_show';
  if (s.includes('no reply') || s.includes('vm') || s.includes('sms') || s === 'no_reply' || s === 'no answer') return 'no_reply';

  return s;
}

export function getStatusConfig(rawStatus: string | null | undefined): StatusConfig {
  if (!rawStatus) return FALLBACK;
  const key = normalize(rawStatus);
  return STATUS_MAP[key] || FALLBACK;
}

export interface DropdownOption {
  value: string;
  label: string;
  color: string;
  bg: string;
  border: string;
}

export const DROPDOWN_OPTIONS: DropdownOption[] = [
  { value: 'scheduled', label: 'Planifié', color: 'text-blue-700', bg: 'bg-blue-100', border: 'border-blue-200' },
  { value: 'confirmed', label: 'Confirmé', color: 'text-cyan-700', bg: 'bg-cyan-100', border: 'border-cyan-200' },
  { value: 'showed', label: 'Effectué', color: 'text-emerald-700', bg: 'bg-emerald-100', border: 'border-emerald-200' },
  { value: 'no_show', label: 'No-show', color: 'text-rose-800', bg: 'bg-rose-100', border: 'border-rose-300' },
  { value: 'cancelled', label: 'Annulé', color: 'text-red-700', bg: 'bg-red-100', border: 'border-red-200' },
  { value: 'rescheduled', label: 'Reporté', color: 'text-amber-700', bg: 'bg-amber-100', border: 'border-amber-200' },
  { value: 'no_reply', label: 'Pas de réponse', color: 'text-gray-700', bg: 'bg-gray-100', border: 'border-gray-300' },
];

export const FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: 'all', label: 'Tous les statuts' },
  { value: 'scheduled', label: 'Planifiés' },
  { value: 'confirmed', label: 'Confirmés' },
  { value: 'showed', label: 'Effectués' },
  { value: 'no_show', label: 'No-show' },
  { value: 'cancelled', label: 'Annulés' },
  { value: 'rescheduled', label: 'Reportés' },
  { value: 'no_reply', label: 'Pas de réponse' },
];

export function matchesStatusFilter(rawStatus: string, filterValue: string): boolean {
  if (filterValue === 'all') return true;
  return normalize(rawStatus) === filterValue;
}
